import { ProductT } from '@lambda/apis/src/catalog-v2/types';
import Link from 'next/link';
import Image from 'next/image';
import classNames from 'classnames';
import { priceFormater } from '@/settings/index';
import { priceFormatSuperscript } from '@/lib/price';
import i18n from '@/settings/i18n';

const t = i18n.init('shared');

const ProductCard = ({
  product,
  className,
  index,
  includeSkuId = true,
  titleTag: TitleTag = 'h4',
}: {
  product: ProductT;
  className: string;
  index: number;
  includeSkuId?: boolean;
  /**
   * A product name is a heading on a listing page, where the products are the
   * content, but not in a recommendation carousel, where it is a link label
   * beside the real content.
   */
  titleTag?: 'h4' | 'div';
}) => {
  const sku = product.skus?.[0];
  const offer = sku?.offers?.[0];

  if (!offer || !sku) {
    return null;
  }

  const productId = product.slug;
  const skuId = sku.id;

  const image =
    product.image?.url ??
    product.images?.[0]?.url ??
    product.skus?.[0]?.image?.url ??
    product?.skus?.[0]?.images?.[0]?.url ??
    null;

  const productName = product.title ?? product.name ?? '';
  const offerPrice = (product.skus?.[0]?.offers?.[0]?.price ?? 0) / 100;

  // Only link to a specific SKU when variant filters are applied
  const link = `/collections/${encodeURIComponent(productId)}${
    includeSkuId ? `?skuId=${encodeURIComponent(skuId)}` : ''
  }`;

  return (
    <Link
      href={link}
      key={product.id}
      className={classNames(
        'flex flex-col bg-white p-4 text-center focus:outline-none',
        className,
      )}
    >
      <div className="w-full">
        {image ? (
          <div>
            <Image
              src={image}
              alt={productName ?? t`Product Image`}
              width="280"
              height="120"
              sizes="70px"
              loading={index === 0 ? 'eager' : 'lazy'}
              className="mx-auto h-[18vw] max-w-[130px] object-contain xs:h-[13vw] sm:h-[10vw] lg:h-[7vw] xl:h-[95px]"
            />
          </div>
        ) : (
          <div className="flex h-[18vw] animate-pulse items-center justify-center bg-gray-700/5 xs:h-[13vw] sm:h-[10vw] lg:h-[7vw] xl:h-[95px]" />
        )}
      </div>
      <TitleTag
        // eslint-disable-next-line tailwindcss/no-custom-classname
        className="two-line-ellipsis pt-5 text-xs font-bold !leading-4 xs:pt-6 sm:text-sm"
      >
        {productName}
      </TitleTag>
      <div className="mt-1 text-xs sm:text-sm">
        {t`From`}
        <span className="ml-1" translate="no">
          {priceFormatSuperscript(priceFormater.long(offerPrice))}
        </span>
      </div>
    </Link>
  );
};

export default ProductCard;
